import { type ComponentChildren } from 'preact';
import { useEffect, useId, useMemo, useRef, useState } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { SearchIcon } from '@xantory/icons';
import { useT } from '@xantory/i18n';
import { Scrim } from '../Scrim/scrim';
import { useMountTransition } from '../_hooks/use-mount-transition';
import type { NavSection } from '../Sidebar/nav-tree';
import { styles } from './command-palette.styles';

/** One reachable destination. Flattened from the nav tree — never declared twice. */
interface Entry {
  key: string;
  label: string;
  href: string;
  /** Section title, plus the group it sits under when it has one. */
  section: string;
  /** The group's label, shown on the right so two same-named leaves differ. */
  group?: string | undefined;
  icon: ComponentChildren;
}

/** A record hit supplied by the app layer (`ui` never fetches — packages.md). */
export interface CommandRecord {
  key: string;
  /** The identifier an operator would actually type: INC-1024, AST-0031. */
  number: string;
  title: string;
  /** Already-localised noun for the kind of record ("Incident", "Asset"). */
  type: string;
  href: string;
}
interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /**
   * The SAME resolved nav tree the sidebar renders (R-346).
   *
   * ⚠ This is the whole point of feeding it from here rather than from a list of
   * its own: `buildRegistry()` has already dropped every feature the deploy mode
   * or the user's permissions disable, and the shell has already localised the
   * labels. A palette with its own route list would be a second registry that
   * drifts — and the way it would drift is by offering an operator a page they
   * are not allowed to open.
   */
  sections: NavSection[];
  /** Route key of the page currently open — marked, and never the first hit. */
  activeKey: string;
  /**
   * Matching RECORDS for the current query, listed above the pages.
   *
   * The app layer computes these because finding them means reading the query
   * caches, and a `ui` component that loads its own data can neither have a story
   * nor be reused (`packages.md`). Pair with `onQueryChange`.
   */
  records?: CommandRecord[] | undefined;
  /** Localised heading for the records group. */
  recordsLabel?: string | undefined;
  /** Fires on every keystroke so the app layer can recompute `records`. */
  onQueryChange?: ((query: string) => void) | undefined;
}

/** Depth-first over the tree, so palette order == sidebar order. */
function flatten(sections: NavSection[]): Entry[] {
  const out: Entry[] = [];
  for (const sec of sections) {
    for (const node of sec.items) {
      if (node.kind === 'leaf') {
        out.push({
          key: node.key,
          label: node.label,
          href: node.href,
          section: sec.title,
          icon: node.icon
        });
        continue;
      }
      for (const child of node.children) {
        out.push({
          key: child.key,
          label: child.label,
          href: child.href,
          section: sec.title,
          group: node.label,
          icon: node.icon
        });
      }
    }
  }
  return out;
}

/**
 * Subsequence match — "mcr" finds "Microcontrollers", "frmw" finds "Firmware". A palette people reach for with one hand has to forgive the letters
 * they did not type; a plain `includes()` would answer "no matches" for both of
 * those and send the operator back to the sidebar, which is the one outcome that
 * makes the feature pointless.
 *
 * Returns a score (lower is better) or `null` for no match: a contiguous run
 * beats a scattered one, and a hit at the start of the label beats one in the
 * middle, so typing "sen" puts Sensors above Device Sensors.
 */
function score(haystack: string, needle: string): number | null {
  if (needle === '') return 0;
  let hay = 0;
  let gaps = 0;
  let first = -1;
  let prev = -1;
  for (const ch of needle) {
    const at = haystack.indexOf(ch, hay);
    if (at === -1) return null;
    if (first === -1) first = at;
    if (prev !== -1 && at !== prev + 1) gaps += 1;
    prev = at;
    hay = at + 1;
  }
  return gaps * 10 + first;
}

/**
 * The ⌘K / Ctrl-K command palette: every page the signed-in operator can reach,
 * by name, without walking the sidebar.
 *
 * Deliberately NAVIGATION only. `GlobalSearch` in the topbar searches RECORDS
 * (incident numbers, asset tags) across the loaded caches; this searches PAGES.
 * Folding the two together was considered and rejected — one of them can answer
 * instantly from the nav tree and the other cannot, so a single box would make
 * the fast half feel as slow as the slow half.
 */
export function CommandPalette({
  open,
  onClose,
  sections,
  activeKey,
  records,
  recordsLabel,
  onQueryChange
}: CommandPaletteProps) {
  const t = useT('ui');
  const {
    mounted,
    show
  } = useMountTransition(open);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const baseId = useId();
  // Held in a ref so a caller passing an inline arrow — the normal case — cannot
  // turn the reset effect below into a render loop (same reason `Table` holds
  // `onSortChange` in one).
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;
  const pages = useMemo(() => flatten(sections), [sections]);

  // Records first, and NOT re-ranked against the pages: somebody who typed a
  // ticket number wants the ticket, and letting a page called "Incidents" outrank
  // INC-1024 because the word matched better is how a palette starts feeling
  // wrong without anyone being able to say why.
  const recordEntries = useMemo<Entry[]>(() => (records ?? []).map(r => ({
    key: `rec:${r.key}`,
    label: `${r.number} · ${r.title}`,
    href: r.href,
    section: recordsLabel ?? '',
    group: r.type,
    icon: null
  })), [records, recordsLabel]);
  const pageHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pages;
    return pages.map(e => {
      // Match against the label AND its section/group, so "iot" surfaces every
      // page under IoT Devices even when the word is not in the page's own name.
      const label = score(e.label.toLowerCase(), q);
      const context = score(`${e.section} ${e.group ?? ''} ${e.label}`.toLowerCase(), q);
      if (label == null && context == null) return null;
      // A label hit always outranks a context-only hit, whatever their scores.
      return {
        e,
        rank: label != null ? label : 1000 + (context ?? 0)
      };
    }).filter((x): x is {
      e: Entry;
      rank: number;
    } => x != null).sort((a, b) => a.rank - b.rank).map(x => x.e);
  }, [pages, query]);
  const hits = useMemo(() => [...recordEntries, ...pageHits], [recordEntries, pageHits]);

  // A fresh open is a fresh search — reopening onto the last query is the palette
  // lying about what it is showing.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCursor(0);
    onQueryChangeRef.current?.('');
  }, [open]);

  // The input is the only thing anyone wants focused here, so this takes focus
  // directly rather than using `useFocusTrap` (whose job is to pick the FIRST
  // focusable, which would be right for a form and wrong for a search box).
  // Retried across frames for the same reason the trap retries: the panel starts
  // `visibility: hidden` for its enter transition, and focus() no-ops on that.
  useEffect(() => {
    if (!show) return;
    const prev = document.activeElement as HTMLElement | null;
    let raf = 0;
    const tryFocus = (left: number) => {
      inputRef.current?.focus();
      if (document.activeElement !== inputRef.current && left > 0) {
        raf = requestAnimationFrame(() => tryFocus(left - 1));
      }
    };
    tryFocus(10);
    return () => {
      cancelAnimationFrame(raf);
      prev?.focus();
    };
  }, [show]);

  // Keep the keyboard cursor in view — arrowing past the fold must scroll.
  useEffect(() => {
    listRef.current?.querySelector(`[data-cmd-index="${cursor}"]`)?.scrollIntoView({
      block: 'nearest'
    });
  }, [cursor]);
  if (!mounted) return null;

  /** ONE navigation path: the rows are real links, and Enter clicks the row it is
   *  on. A second programmatic `navigate()` here would be a second place the
   *  palette could send somebody, and the two would drift. */
  const go = (index: number) => {
    const row = listRef.current?.querySelector<HTMLAnchorElement>(`[data-cmd-index="${index}"]`);
    if (!row) return;
    onClose();
    row.click();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor(i => hits.length === 0 ? 0 : (i + 1) % hits.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(i => hits.length === 0 ? 0 : (i - 1 + hits.length) % hits.length);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setCursor(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setCursor(Math.max(0, hits.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(cursor);
    }
  };

  // Grouped for display, but the keyboard cursor indexes the FLAT list — so the
  // number on a row and the number the arrow keys count are the same number.
  const grouped: {
    section: string;
    rows: {
      entry: Entry;
      index: number;
    }[];
  }[] = [];
  hits.forEach((entry, index) => {
    const last = grouped[grouped.length - 1];
    if (last && last.section === entry.section) last.rows.push({
      entry,
      index
    });else grouped.push({
      section: entry.section,
      rows: [{
        entry,
        index
      }]
    });
  });
  const activeId = hits.length > 0 ? `${baseId}-opt-${cursor}` : undefined;
  return <>
      <Scrim show={show} onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={t('commandPaletteTitle')} {...stylex.props(styles.panel, show && styles.show)}>
        <div {...stylex.props(styles.searchRow)}>
          <SearchIcon size={18} />
          <input ref={inputRef} type="text" role="combobox"
        // Both track whether a listbox is actually on the page: `aria-controls`
        // pointing at an id that is not rendered is a dangling reference, and
        // `aria-expanded="true"` with nothing to expand into is a false claim.
        aria-expanded={hits.length > 0} aria-controls={hits.length > 0 ? `${baseId}-list` : undefined} aria-activedescendant={activeId} aria-label={t('commandPaletteTitle')} autocomplete="off" placeholder={t('commandPalettePlaceholder')} value={query} onInput={e => {
          const next = (e.target as HTMLInputElement).value;
          setQuery(next);
          setCursor(0);
          onQueryChangeRef.current?.(next);
        }} onKeyDown={onKeyDown} {...stylex.props(styles.input)} />
          <span {...stylex.props(styles.kbd)}>Esc</span>
        </div>

        {/* ⚠ The listbox is only rendered when it HAS options. A `role="listbox"`
            holding nothing but an empty-state paragraph fails axe's
            `aria-required-children` (critical) — and it is also a lie: it tells a
            screen reader there is a list to arrow through when there is not. */}
        {hits.length === 0 ? <div ref={listRef} {...stylex.props(styles.list)}>
            <div {...stylex.props(styles.empty)}>{t('commandPaletteEmpty', {
            query: query.trim()
          })}</div>
          </div> : <div ref={listRef} id={`${baseId}-list`} role="listbox" {...stylex.props(styles.list)}>
            {grouped.map(g => <div key={g.section} role="group" aria-label={g.section} {...stylex.props(styles.group)}>
                {/* aria-hidden: `listbox > group` may contain OPTIONS only, so the
                    visible heading has to be invisible to AT. The group's own
                    `aria-label` carries the same words, so nothing is lost. */}
                <div aria-hidden="true" {...stylex.props(styles.groupLabel)}>
                  {g.section}
                </div>
                {g.rows.map(({
            entry,
            index
          }) => {
            const current = entry.key === activeKey;
            return <a key={entry.key} id={`${baseId}-opt-${index}`} role="option" aria-selected={index === cursor} aria-current={current ? 'page' : undefined} data-cmd-index={index} href={entry.href}
            // Closing here as well as in `go()` covers the mouse path:
            // a click never went through the keyboard handler.
            onClick={onClose} onMouseMove={() => setCursor(index)} {...stylex.props(styles.row, index === cursor && styles.rowActive)}>
                      <span aria-hidden="true" {...stylex.props(styles.rowIcon)}>
                        {entry.icon}
                      </span>
                      <span {...stylex.props(styles.rowLabel, current && styles.rowCurrent)}>
                        {entry.label}
                      </span>
                      {entry.group != null && <span {...stylex.props(styles.rowPath)}>{entry.group}</span>}
                    </a>;
          })}
              </div>)}
          </div>}

        <div {...stylex.props(styles.foot)}>
          <span>{t('commandPaletteHint')}</span>
        </div>
      </div>
    </>;
}